import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Sélection d'une collection (ADR-096 §4), portée par le conteneur et partagée par toutes les
 * représentations. Deux formes : des identifiants choisis un à un, ou **tout l'ensemble filtré**
 * (la requête gouvernée elle-même, figée au lancement de l'action de masse) — jamais « la page ».
 * Le conteneur la vide quand la requête change et après une action de masse.
 *
 * Gestes (ADR-149 §2) : `pick` lit les touches du clic — Maj : la plage depuis le dernier élément
 * choisi (dans l'ordre affiché) ; Ctrl ou Cmd : ajouter ou retirer ; sans touche : `toggle`, ou
 * `replace` quand un clic sur la carte sélectionne (présentation drive).
 */

/** Touches d'un clic : celles de `MouseEvent` et de `KeyboardEvent`. */
export interface PickModifiers {
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
}

export interface Selection {
  mode: 'ids' | 'all';
  /** Nombre sélectionné : ids choisis, ou total de l'ensemble filtré. */
  count: number;
  ids: ReadonlySet<string>;
  has(id: string): boolean;
  toggle(id: string): void;
  add(ids: readonly string[]): void;
  /** Remplace la sélection par ces identifiants (Ctrl+A, clic simple en drive). */
  replace(ids: readonly string[]): void;
  /** Geste de sélection sur un élément affiché : plage, ajout ou retrait, ou choix seul. */
  pick(id: string, modifiers: PickModifiers, plain?: 'toggle' | 'replace'): void;
  clear(): void;
  selectAll(): void;
  /** Couverture d'un groupe de lignes affichées : aucune, une partie, toutes. */
  coverage(ids: readonly string[]): 'none' | 'some' | 'all';
}

/**
 * Nouvelle sélection après un geste (fonction pure, testée seule). La plage va de l'ancre à l'élément,
 * dans l'ordre affiché ; elle s'ajoute à la sélection. Sans ancre connue, Maj+clic agit comme un clic.
 */
export function gesture(order: readonly string[], current: ReadonlySet<string>, anchor: string | null, id: string, modifiers: PickModifiers, plain: 'toggle' | 'replace' = 'toggle'): { ids: Set<string>; anchor: string } {
  const from = anchor === null ? -1 : order.indexOf(anchor);
  const to = order.indexOf(id);
  if (modifiers.shiftKey === true && from >= 0 && to >= 0) {
    const [start, end] = from <= to ? [from, to] : [to, from];
    return { ids: new Set([...current, ...order.slice(start, end + 1)]), anchor: anchor! };
  }
  if (modifiers.ctrlKey === true || modifiers.metaKey === true || plain === 'toggle') {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return { ids: next, anchor: id };
  }
  return { ids: new Set([id]), anchor: id };
}

export function useSelection(total: number | undefined, order: readonly string[] = [], options: { range?: boolean } = {}): Selection {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());
  const [all, setAll] = useState(false);
  const anchor = useRef<string | null>(null);
  const orderRef = useRef(order);
  orderRef.current = order;
  const range = options.range !== false;
  const toggle = useCallback((id: string) => {
    setAll(false);
    anchor.current = id;
    setIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const add = useCallback((more: readonly string[]) => {
    setAll(false);
    setIds((current) => new Set([...current, ...more]));
  }, []);
  const replace = useCallback((next: readonly string[]) => {
    setAll(false);
    setIds(new Set(next));
  }, []);
  const pick = useCallback(
    (id: string, modifiers: PickModifiers, plain: 'toggle' | 'replace' = 'toggle') => {
      setAll(false);
      // Le calcul ne dépend que de la sélection courante et de l'ancre (inchangée par une plage) : il
      // peut être rejoué sans effet de plus.
      setIds((current) => {
        const next = gesture(orderRef.current, current, anchor.current, id, range ? modifiers : { ...modifiers, shiftKey: false }, plain);
        anchor.current = next.anchor;
        return next.ids;
      });
    },
    [range],
  );
  const clear = useCallback(() => {
    setAll(false);
    anchor.current = null;
    setIds(new Set());
  }, []);
  const selectAll = useCallback(() => setAll(true), []);
  return useMemo<Selection>(
    () => ({
      mode: all ? 'all' : 'ids',
      count: all ? (total ?? 0) : ids.size,
      ids,
      has: (id) => all || ids.has(id),
      toggle,
      add,
      replace,
      pick,
      clear,
      selectAll,
      coverage: (shown) => {
        if (shown.length === 0) return 'none';
        const inside = all ? shown.length : shown.filter((id) => ids.has(id)).length;
        return inside === 0 ? 'none' : inside === shown.length ? 'all' : 'some';
      },
    }),
    [all, ids, total, toggle, add, replace, pick, clear, selectAll],
  );
}

/** Le clavier vise-t-il un champ de saisie ? Ctrl+A et Échap y gardent leur sens. */
export function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['TEXTAREA', 'SELECT'].includes(target.tagName) || (target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'checkbox');
}

/**
 * Raccourcis de sélection d'une zone de liste (ADR-149 §2) : Ctrl+A (ou Cmd+A) sélectionne tout ce qui
 * est affiché, Échap vide. À poser sur le conteneur de la liste ; ignoré dans un champ de saisie.
 */
export function selectionKeys(selection: Pick<Selection, 'replace' | 'clear' | 'count'>, shown: () => readonly string[]): (event: { key: string; ctrlKey: boolean; metaKey: boolean; target: EventTarget | null; preventDefault: () => void }) => void {
  return (event) => {
    if (typingIn(event.target)) return;
    if ((event.ctrlKey || event.metaKey) && (event.key === 'a' || event.key === 'A')) {
      event.preventDefault();
      selection.replace(shown());
    } else if (event.key === 'Escape' && selection.count > 0) {
      event.preventDefault();
      selection.clear();
    }
  };
}
